import { useEffect, useState } from 'react';

/**
 * The generated example photographs, keyed as the generator writes them:
 * `<povId>`, `pose-<id>`, `scene-<id>`, `light-<id>`, `grade-<id>`.
 *
 * Fetched once and shared, rather than each picker fetching the same manifest.
 * Absent until `npm run examples` has been run, so every card must still be
 * legible without one.
 */
let cache: Record<string, string> | undefined;
let inFlight: Promise<Record<string, string>> | undefined;

function load(): Promise<Record<string, string>> {
  if (cache) return Promise.resolve(cache);
  inFlight ??= fetch('/examples/manifest.json')
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((m: Record<string, string>) => {
      cache = m;
      return m;
    });
  return inFlight;
}

export function useExamples(): Record<string, string> {
  const [manifest, setManifest] = useState<Record<string, string>>(cache ?? {});
  useEffect(() => {
    let live = true;
    void load().then((m) => live && setManifest(m));
    return () => {
      live = false;
    };
  }, []);
  return manifest;
}

/**
 * Where a card loads its example from: a thumbnail the server makes at the
 * card's size (a full render is a megabyte or more, and a picker shows dozens),
 * or the file itself for a format the server does not resize.
 */
export const exampleUrl = (file: string) =>
  /\.(png|jpe?g)$/i.test(file) ? `/api/examples/thumb/${encodeURIComponent(file)}` : `/examples/${file}`;
